import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { FaArrowUp } from "react-icons/fa";
import Footer from "../components/footer.tsx";
import Header from "../components/header.tsx";
import emailjs from "emailjs-com";

export default function Home() {
  const [showPopup, setShowPopup] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const form = useRef();

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const sendEmail = (e) => {
    e.preventDefault();

    emailjs
      .sendForm(
        "service_anba3qi",
        "template_jjy3bva",
        form.current,
        "pBdRpt6V-xkPiaE2h"
      )
      .then(() => {
        setShowPopup(true);
        form.current.reset();
        setTimeout(() => setShowPopup(false), 5000);
      })
      .catch((error) => {
        alert("Une erreur est survenue : " + error.text);
      });
  };

  const services = [
    {
      title: "Clarifiez votre message",
      desc: "Nous transformons la confusion en clarté pour que vos clients comprennent enfin ce que vous proposez.",
      image: "/images/message.png",
    },
    {
      title: "Créez un contenu percutant",
      desc: "Touchez votre audience avec un message émotionnel et une narration de marque.",
      image: "/images/contenue.png",
    },
    {
      title: "Améliorez vos conversions",
      desc: "Transformez vos prospects en clients fidèles grâce à des stratégies éprouvées.",
      image: "/images/strategy.png",
    },
  ];

  const partners = [
    { name: "BKS Formation", logo: "/logos/bks.png" },
    { name: "OSER Formation", logo: "/logos/oser.png" },
    { name: "EDUCASER", logo: "/logos/educaser.png" },
  ];

  return (
    <div className="bg-lightgreen text-darker scroll-smooth">
      {/* Header */}
      <Header />

      {/* Hero Section with Wave Brush Border */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className={`
    relative flex flex-col items-center justify-center
    text-center px-4 py-24 lg:py-36

    bg-softgreen            /* fallback color */
    bg-cover bg-center      /* cover + center */
    bg-no-repeat            /* no tile */

    before:absolute before:inset-0 before:bg-black/50 before:z-10
    overflow-hidden
  `}
        style={{
          backgroundImage: "url('/images/hero.jpg')",
        }}
      >
        {/* z-20 content */}
        <h1 className="z-20 text-4xl text-white md:text-6xl font-bold …">
          Racontez une histoire qui vend
        </h1>
        <p className="mt-6 text-lg md:text-xl max-w-2xl text-white transition duration-300 hover:opacity-80 z-20">
          Chez Story Brand, nous aidons les entreprises à clarifier leur
          message, à engager leurs clients et à augmenter leur chiffre
          d’affaires grâce à des cadres de narration puissants.
        </p>
        <a
          href="#services"
          className="mt-10 inline-block bg-primary text-dark font-semibold py-3 px-6 rounded-full shadow hover:bg-yellow-400 transition-all duration-300 ease-in-out hover:scale-105 transform z-20"
        >
          Découvrez notre méthode
        </a>

        {/* SVG Wave Brush at Bottom */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
          <svg
            viewBox="0 0 1200 100"
            preserveAspectRatio="none"
            className="w-full h-24"
          >
            <path
              d="M0,16 C150,64 350,0 600,16 C850,32 1050,0 1200,16 L1200,100 L0,100 Z"
              fill="#E1E8D6"
            />
          </svg>
        </div>
      </motion.section>

      {/* Services Section */}
      <motion.section
        id="services"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="px-4 py-20 bg-lightgreen"
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2
            style={{ fontFamily: "'Jour Serif', serif" }}
            className="text-3xl font-bold mb-12 transition duration-300 "
          >
            Nos Services
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {services.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  ease: "easeOut",
                  delay: 0.2 + idx * 0.1,
                }}
                viewport={{ once: true }}
                className="p-6 border rounded-2xl shadow bg-softgreen text-dark hover:shadow-2xl hover:-translate-y-1 hover:scale-[1.02] transition"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-auto object-cover rounded-lg mb-4"
                />
                <h3
                  style={{ fontFamily: "'Jour Serif', serif" }}
                  className="font-semibold text-xl mb-2"
                >
                  {item.title}
                </h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Accompagnement Section */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="py-20 px-4 bg-softgreen text-dark"
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8">
          <div className="md:w-1/2">
            <img
              src="/images/help.png"
              alt="Accompagnement"
              className="w-full rounded-2xl shadow-lg object-cover"
            />
          </div>
          <div className="md:w-1/2 text-center md:text-left">
            <h2
              style={{ fontFamily: "'Jour Serif', serif" }}
              className="text-3xl font-bold mb-6"
            >
              Comment Story Brand vous accompagne
            </h2>
            <p className="text-lg transition duration-300 hover:opacity-80">
              Nous analysons vos besoins, clarifions votre message et
              construisons une stratégie de communication alignée avec vos
              objectifs.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Partenaires Section */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="py-20 px-4 bg-lightgreen text-dark"
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2
            style={{ fontFamily: "'Jour Serif', serif" }}
            className="text-3xl font-bold mb-10 transition duration-300"
          >
            Nos partenaires
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 justify-center items-center">
            {partners.map((partner, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  delay: 0.1 + index * 0.1,
                }}
                viewport={{ once: true }}
                className="w-40 h-40 flex flex-col items-center justify-center bg-dark rounded-2xl shadow hover:shadow-lg transition mx-auto p-4"
              >
                {/* Bloc fixe pour le logo (carré 24x24) */}
                <div className="w-24 h-24 flex items-center justify-center  rounded-lg mb-2">
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                {/* Nom du partenaire */}
                <p className="text-sm font-medium text-white">{partner.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Assurance Formation Section */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="py-20 px-4 bg-softgreen text-dark"
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8">
          <div className="md:w-1/2 text-center md:text-left">
            <h2
              style={{ fontFamily: "'Jour Serif', serif" }}
              className="text-3xl font-bold mb-6 transition duration-300"
            >
              Formations en lien avec les assurances
            </h2>
            <p className="text-lg transition duration-300 hover:opacity-80">
              Nous travaillons avec des compagnies d’assurances pour former les
              professionnels à la gestion de la relation client et à la
              communication en situation de crise.
            </p>
          </div>
          <div className="md:w-1/2">
            <img
              src="/images/assurance.png"
              alt="Formations Assurances"
              className="w-full rounded-2xl shadow-lg object-cover"
            />
          </div>
        </div>
      </motion.section>

      {/* À propos Section */}
      <motion.section
        id="about"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
        viewport={{ once: true }}
        className="bg-lightgreen py-20 px-4"
      >
        <div className="max-w-4xl mx-auto bg-softgreen rounded-2xl shadow-lg overflow-hidden md:flex">
          {/* Illustration à gauche (sur desktop), ou au-dessus (sur mobile) */}
          <div className="md:w-1/3 bg-lightgreen flex items-center justify-center p-6">
            <img
              src="/images/team.png"
              alt="Équipe Story Brand"
              className="w-32 h-32 object-cover rounded-full shadow-md"
            />
          </div>

          {/* Texte à droite (ou dessous sur mobile) */}
          <div className="md:w-2/3 p-8">
            <h2
              style={{ fontFamily: "'Jour Serif', serif" }}
              className="text-3xl sm:text-4xl font-bold text-dark mb-4"
            >
              À propos de nous
            </h2>
            {/* Ligne décorative sous le titre */}
            <div className="w-20 h-1 bg-primary mb-6 rounded mx-auto md:mx-0"></div>
            <p className="text-lg text-gray-800 leading-relaxed">
              Story Brand est une équipe de marketeurs, de rédacteurs et de
              designers qui croient que chaque entreprise a une histoire qui
              mérite d'être racontée. Notre mission est de vous aider à créer un
              message de marque clair et percutant qui touche votre public
              cible.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Pourquoi Nous Section */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="bg-lightgreen py-20 px-4"
      >
        <div className="max-w-3xl mx-auto bg-softgreen rounded-2xl shadow-md p-8">
          <h2
            style={{ fontFamily: "'Jour Serif', serif" }}
            className="text-3xl sm:text-4xl font-bold text-dark text-center mb-3"
          >
            Pourquoi choisir Story Brand ?
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mb-6 rounded"></div>
          <p className="text-lg text-gray-800 leading-relaxed text-center mb-8">
            Parce que nous croyons que chaque entreprise mérite une stratégie de
            communication claire, humaine et efficace. Notre méthode repose sur
            l’écoute des besoins, l’empathie vis-à-vis de vos clients, et la
            puissance du storytelling pour générer plus de conversions et
            fidéliser sur le long terme.
          </p>

          {/* Optionnel : trois points forts en colonnes (sur mobile empilés, sur desktop côte à côte) */}
          <div className="grid gap-6 sm:grid-cols-3">
            <div className="flex flex-col items-center text-center">
              <img
                src="/images/strat.png"
                alt="Stratégie"
                className="w-12 h-12 mb-3"
              />
              <h3
                style={{ fontFamily: "'Jour Serif', serif" }}
                className="text-xl font-semibold text-dark mb-1"
              >
                Stratégie claire
              </h3>
              <p className="text-gray-700 text-sm">
                Un plan précis, adapté à vos objectifs et à votre marché.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <img
                src="/images/contact.png"
                alt="Empathie"
                className="w-12 h-12 mb-3"
              />
              <h3
                style={{ fontFamily: "'Jour Serif', serif" }}
                className="text-xl font-semibold text-dark mb-1"
              >
                Approche humaine
              </h3>
              <p className="text-gray-700 text-sm">
                Chaque message se construit autour des besoins réels de vos
                clients.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <img
                src="/images/result.png"
                alt="Résultats"
                className="w-12 h-12 mb-3"
              />
              <h3
                style={{ fontFamily: "'Jour Serif', serif" }}
                className="text-xl font-semibold text-dark mb-1"
              >
                Résultats mesurables
              </h3>
              <p className="text-gray-700 text-sm">
                Un suivi précis pour optimiser en continu votre ROI.
              </p>
            </div>
          </div>
        </div>
      </motion.section>
      {/* Section de Contact */}
      <motion.section
        id="contact"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="min-h-screen bg-softgreen py-20 px-4 text-dark"
      >
        <div className="max-w-2xl mx-auto text-center">
          <h2
            style={{ fontFamily: "'Jour Serif', serif" }}
            className="text-4xl font-bold text-dark mb-4 transition duration-300 hover:text-primary"
          >
            Contactez-nous
          </h2>
          <p className="mb-10 transition duration-300 hover:opacity-80">
            Nous serions ravis de vous entendre ! Remplissez le formulaire
            ci-dessous.
          </p>
          <form
            ref={form}
            onSubmit={sendEmail}
            className="bg-white p-8 shadow-lg rounded-xl space-y-6"
          >
            <div className="text-left">
              <label htmlFor="name" className="block text-sm font-medium mb-1">
                Entreprise
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="w-full px-4 py-2 border rounded-full focus:ring-2 focus:ring-primary transition duration-200"
                required
              />
            </div>
            <div className="text-left">
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Adresse e-mail
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="w-full px-4 py-2 border rounded-full focus:ring-2 focus:ring-primary transition duration-200"
                required
              />
            </div>
            <div className="text-left">
              <label
                htmlFor="message"
                className="block text-sm font-medium mb-1"
              >
                Votre message
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-primary transition duration-200"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-primary text-dark py-3 rounded-full font-semibold hover:bg-yellow-400 transition"
            >
              Envoyer le message
            </button>
          </form>
          {showPopup && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="fixed top-6 right-6 z-50 bg-green-100 border border-green-400 text-green-800 px-6 py-4 rounded shadow-lg"
            >
              ✅ Merci ! Votre message a bien été reçu. Nous vous contacterons
              sous 24 à 48h.
            </motion.div>
          )}
        </div>
      </motion.section>

      {/* Scroll To Top Button */}
      <motion.button
        onClick={scrollToTop}
        initial={{ opacity: 0, y: 100 }}
        animate={showScrollTop ? { opacity: 1, y: 0 } : { opacity: 0, y: 100 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-6 right-6 z-50 p-3 bg-primary text-dark rounded-full shadow-lg hover:bg-yellow-400 transition-all"
        aria-label="Revenir en haut"
      >
        <FaArrowUp />
      </motion.button>

      {/* Footer */}
      <Footer />
    </div>
  );
}
